import uniqBy from 'lodash/uniqBy';

import { addAltTagToProductTemplateCarouselImages } from 'productTemplate/utils/addAltTagToProductTemplateCarouselImages';
import { getRobotProductImagesForCarousel } from 'productTemplate/utils/getProductRobotImages';

/**
 * [getProductTemplateCarouselImages]
 * Returns an Array of image objects, for use in the PDP image carousel.
 *
 * In most cases, we ignore 'mainPictureUrl' and simply return the Array of external pictures.
 * However, if `internalShot` !== 'taken', we prepend `mainPictureUrl` to the Array.
 *
 * In those cases, it's likely a newer product, and GOAT hasn't had the chance to produce
 * professional photography in their studio -- so we fall back to a publicly available image.
 *
 * We also get the robot images of the product, and if showRobots is true, append this
 * to the returned array
 *
 * @param   {string}  internalShot                         'taken' | 'needs_to_take' | 'skip'
 * @param   {string}  mainPictureUrl                       Included as first image if internalShot !== 'taken'
 * @param   {Array}   [productTemplateExternalPictures=[]] Array of images, usually produced by GOAT Content team
 * @param   {Array}   [robotAssets=[]]                     Array of robot images associated with the product
 * @param   {boolean} isRobotsUpdateEnabled                include the robot back images and videos in the carousel images
 * @param   {boolean} showRobots                           Determines whether to include robot images in carousel
 * @returns {Array}
 */

export const getProductTemplateCarouselImages = ({
  productTemplate: {
    mainPictureUrl,
    productCategory,
    productTemplateExternalPictures = [],
    robotAssets = [],
    name = '',
  },
  enablePdpImgAltTag,
  showRobots = false,
  robotsCutOff = 3,
}) => {
  const images = [...productTemplateExternalPictures];
  const mainImage = {
    mainPictureUrl,
  };

  if (!mainPictureUrl?.includes('missing.png') && images.length === 0) {
    images.unshift(mainImage);
  }

  // add alt text for each unique image url
  const dedupedImages = enablePdpImgAltTag
    ? addAltTagToProductTemplateCarouselImages({
        images: uniqBy(images, 'mainPictureUrl'),
        name,
        productCategory,
      })
    : uniqBy(images, 'mainPictureUrl');

  if (!showRobots) return dedupedImages;

  const mutatedRobotImages = getRobotProductImagesForCarousel({
    robotAssets,
    cutOff: robotsCutOff,
    name,
  });

  return [...dedupedImages, ...mutatedRobotImages];
};
